import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Send, FileText, CheckCircle, Clock, Mail, Phone, RefreshCw, Eye, UserCheck, Archive, ArchiveRestore, Trash2, Pencil, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STANDARD_FORM_COUNT = 7;

const CATEGORY_OPTIONS = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "school_age", label: "School Age" },
  { value: "after_school", label: "After School Program" },
];

// Exact existing Age Group values/labels (mirrors ChildForm.jsx — Full Time only)
const AGE_GROUP_OPTIONS = [
  { value: "prek", label: "PreK" },
  { value: "older_toddler", label: "Older Toddler" },
  { value: "young_toddler", label: "Young Toddler" },
  { value: "infant", label: "Infant" },
];

const DOC_LABELS = {
  shot_records: "Child Shot Records",
  enrollment_info: "Enrollment Information Sheet",
  financial_contract: "Financial & Contract Agreement",
  about_child: "All About Your Child Form",
  health_report: "Child Health Report",
  emergency_consent: "Emergency Contact / Parental Consent Form",
  transportation_policy: "School Age Transportation & Transfer of Care Policy",
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function PendingEnrollmentsPanel({ onAccepted } = {}) {
  const [pending, setPending] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [archived, setArchived] = useState([]);
  const [neverEnrolled, setNeverEnrolled] = useState([]);
  const [archivingId, setArchivingId] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [clearingAllNeverEnrolled, setClearingAllNeverEnrolled] = useState(false);
  const [neverEnrolledTarget, setNeverEnrolledTarget] = useState(null);
  const [neverEnrolledReason, setNeverEnrolledReason] = useState("");
  const [markingNeverEnrolled, setMarkingNeverEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaSMS, setSendViaSMS] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptCategory, setAcceptCategory] = useState("");
  const [acceptSubcategory, setAcceptSubcategory] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ childName: "", parentName: "", parentEmail: "", parentPhone: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", category: "", fullTimeSubcategory: "", birthdate: "", parentName: "", parentEmail: "", parentPhone: "",
  });

  const load = useCallback(async () => {
    try {
      const [p, a, ar, ne] = await Promise.all([
        axios.get(`${API}/pending-enrollment`, authHeaders()),
        axios.get(`${API}/pending-enrollment/accepted`, authHeaders()),
        axios.get(`${API}/pending-enrollment/archived`, authHeaders()),
        axios.get(`${API}/pending-enrollment/archived-pending`, authHeaders()),
      ]);
      setPending(p.data || []);
      setAccepted(a.data || []);
      setArchived(ar.data || []);
      setNeverEnrolled(ne.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load pending enrollments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAccept = (p) => {
    setAcceptTarget(p);
    setAcceptCategory(p.category || "");
    setAcceptSubcategory(p.fullTimeSubcategory || "");
  };

  const openEdit = (p) => {
    setEditTarget(p);
    setEditForm({
      childName: p.childName || "",
      parentName: p.parentName || "",
      parentEmail: p.parentEmail || "",
      parentPhone: p.parentPhone || "",
    });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    if (!editForm.childName.trim()) {
      toast.error("Child name is required");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/pending-enrollment/${editTarget.id}`, {
        childName: editForm.childName.trim(),
        parentName: editForm.parentName.trim() || null,
        parentEmail: editForm.parentEmail.trim() || null,
        parentPhone: editForm.parentPhone.trim() || null,
      }, authHeaders());
      toast.success("Pending enrollment updated");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update pending enrollment");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAccept = async () => {
    if (!acceptTarget) return;
    const needsCategory = !acceptTarget.category;
    if (needsCategory && !acceptCategory) {
      toast.error("Please choose a category to accept this child");
      return;
    }
    const resolvedCategory = acceptTarget.category || acceptCategory;
    setAccepting(true);
    try {
      await axios.post(
        `${API}/pending-enrollment/${acceptTarget.id}/accept`,
        {
          category: acceptCategory || null,
          fullTimeSubcategory: resolvedCategory === "full_time" ? (acceptSubcategory || null) : null,
        },
        authHeaders()
      );
      toast.success(`${acceptTarget.childName} accepted — now a normal child record`);
      setAcceptTarget(null);
      setAcceptCategory("");
      setAcceptSubcategory("");
      load();
      onAccepted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept child");
    } finally {
      setAccepting(false);
    }
  };

  const resetForm = () =>
    setForm({ firstName: "", lastName: "", category: "", fullTimeSubcategory: "", birthdate: "", parentName: "", parentEmail: "", parentPhone: "" });

  const handleCreate = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("First name and last name are required");
      return;
    }
    const childName = `${form.lastName.trim()}, ${form.firstName.trim()}`;
    setSaving(true);
    try {
      await axios.post(`${API}/pending-enrollment`, {
        childName,
        category: form.category || null,
        fullTimeSubcategory: form.category === "full_time" ? (form.fullTimeSubcategory || null) : null,
        birthdate: form.birthdate || null,
        parentName: form.parentName || null,
        parentEmail: form.parentEmail || null,
        parentPhone: form.parentPhone || null,
      }, authHeaders());
      toast.success("Pending enrollment created");
      setShowCreate(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create pending enrollment");
    } finally {
      setSaving(false);
    }
  };

  const requestSend = (p) => {
    if (!p.parentEmail && !p.parentPhone) {
      toast.error("Add a parent email or phone before sending the packet");
      return;
    }
    setSendViaEmail(true);
    setSendViaSMS(true);
    setSendTarget(p);
  };

  const handleSend = async (p) => {
    if (!p.parentEmail && !p.parentPhone) {
      toast.error("Add a parent email or phone before sending the packet");
      return;
    }
    setSendTarget(null);
    setSendingId(p.id);
    try {
      const res = await axios.post(`${API}/pending-enrollment/${p.id}/send`, {
        appUrl: window.location.origin,
        sendEmail: sendViaEmail,
        sendSms: sendViaSMS,
      }, authHeaders());
      const parts = [];
      if (res.data.emailSent) parts.push("email");
      if (res.data.smsSent) parts.push("text");
      toast.success(parts.length ? `Packet sent by ${parts.join(" & ")}` : "Packet sent");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send packet");
    } finally {
      setSendingId(null);
    }
  };

  const handleArchive = async (a) => {
    setArchivingId(a.id);
    try {
      await axios.post(`${API}/pending-enrollment/${a.id}/archive`, {}, authHeaders());
      toast.success(`${a.childName} moved to Archived History`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not archive record");
    } finally {
      setArchivingId(null);
    }
  };

  const handleRestore = async (a) => {
    setArchivingId(a.id);
    try {
      await axios.post(`${API}/pending-enrollment/${a.id}/restore`, {}, authHeaders());
      toast.success(`${a.childName} restored to Accepted History`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore record");
    } finally {
      setArchivingId(null);
    }
  };

  const handleClear = async (a) => {
    if (!window.confirm(
      `Clear "${a.childName}" from the Archived view?\n\n` +
      `This only hides the archive entry. The enrolled child, their records, ` +
      `returned enrollment documents, and compliance data are NOT deleted and stay intact.`
    )) return;
    setArchivingId(a.id);
    try {
      await axios.post(`${API}/pending-enrollment/${a.id}/clear`, {}, authHeaders());
      toast.success(`${a.childName} cleared from the Archived view`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear record");
    } finally {
      setArchivingId(null);
    }
  };

  const handleClearAll = async () => {
    if (archived.length === 0) return;
    if (!window.confirm(
      `Clear all ${archived.length} entr${archived.length === 1 ? "y" : "ies"} from the Archived view?\n\n` +
      `This only hides these archive entries. The enrolled children, their records, ` +
      `returned enrollment documents, and compliance data are NOT deleted and stay intact.`
    )) return;
    setClearingAll(true);
    try {
      const res = await axios.post(`${API}/pending-enrollment/archived/clear`, {}, authHeaders());
      toast.success(`${res.data.cleared ?? 0} archived entr${(res.data.cleared === 1) ? "y" : "ies"} cleared`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear the archive");
    } finally {
      setClearingAll(false);
    }
  };

  const openNeverEnrolled = (p) => {
    setNeverEnrolledTarget(p);
    setNeverEnrolledReason("");
  };

  const submitNeverEnrolled = async () => {
    if (!neverEnrolledTarget) return;
    setMarkingNeverEnrolled(true);
    try {
      await axios.post(`${API}/pending-enrollment/${neverEnrolledTarget.id}/archive`,
        { reason: neverEnrolledReason.trim() || null }, authHeaders());
      toast.success(`${neverEnrolledTarget.childName} marked Never Enrolled — restore anytime from the Never Enrolled list`);
      setNeverEnrolledTarget(null);
      setNeverEnrolledReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark this enrollment");
    } finally {
      setMarkingNeverEnrolled(false);
    }
  };

  const handleRestorePending = async (a) => {
    setArchivingId(a.id);
    try {
      await axios.post(`${API}/pending-enrollment/${a.id}/restore`, {}, authHeaders());
      toast.success(`${a.childName} restored to the active Pending list`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore record");
    } finally {
      setArchivingId(null);
    }
  };

  const handleClearNeverEnrolled = async (a) => {
    if (!window.confirm(
      `Clear "${a.childName}" from the Never Enrolled list?\n\n` +
      `This only hides the entry. Nothing is deleted — any returned documents and the parent link stay intact.`
    )) return;
    setArchivingId(a.id);
    try {
      await axios.post(`${API}/pending-enrollment/${a.id}/clear`, {}, authHeaders());
      toast.success(`${a.childName} cleared from the Never Enrolled list`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear record");
    } finally {
      setArchivingId(null);
    }
  };

  const handleClearAllNeverEnrolled = async () => {
    if (neverEnrolled.length === 0) return;
    if (!window.confirm(
      `Clear all ${neverEnrolled.length} entr${neverEnrolled.length === 1 ? "y" : "ies"} from the Never Enrolled list?\n\n` +
      `This only hides these entries. Nothing is deleted — any returned documents and parent links stay intact.`
    )) return;
    setClearingAllNeverEnrolled(true);
    try {
      const res = await axios.post(`${API}/pending-enrollment/archived-pending/clear`, {}, authHeaders());
      toast.success(`${res.data.cleared ?? 0} entr${(res.data.cleared === 1) ? "y" : "ies"} cleared`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear the list");
    } finally {
      setClearingAllNeverEnrolled(false);
    }
  };

  const ProgressBadge = ({ received, total }) => {
    const all = total > 0 && received >= total;
    const cls = all
      ? "bg-green-100 text-green-700"
      : received > 0
      ? "bg-blue-100 text-blue-700"
      : "bg-amber-100 text-amber-700";
    const Icon = all ? CheckCircle : received > 0 ? FileText : Clock;
    const label = all ? "All forms received" : `${received} of ${total} received`;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}
        data-testid="pending-progress-badge">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-teal-100 shadow-sm p-5 mb-6" data-testid="pending-enrollments-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-teal-600" /> New Child Enrollment (Pending)
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Start a packet for a prospective child before they are formally enrolled. Pending children do not appear on
            rosters or count toward compliance until you accept them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="pending-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white gap-1"
            onClick={() => setShowCreate(true)} data-testid="new-pending-enrollment-btn">
            <UserPlus className="w-4 h-4" /> New Pending Enrollment
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
      ) : pending.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm" data-testid="pending-empty-state">
          No pending enrollments yet. Click “New Pending Enrollment” to start one.
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((p) => {
            const uploaded = p.uploadedDocTypes || [];
            const isOpen = expandedId === p.id;
            return (
              <div key={p.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`pending-row-${p.id}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800" data-testid="pending-child-name">{p.childName}</span>
                      <ProgressBadge received={uploaded.length} total={(p.selectedForms?.length) || STANDARD_FORM_COUNT} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {p.parentEmail && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.parentEmail}</span>}
                      {p.parentPhone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.parentPhone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setExpandedId(isOpen ? null : p.id)}
                      data-testid={`pending-view-btn-${p.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)}
                      data-testid={`pending-edit-btn-${p.id}`} title="Edit name/contact">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white gap-1"
                      disabled={sendingId === p.id} onClick={() => requestSend(p)}
                      data-testid={`pending-send-btn-${p.id}`}>
                      {sendingId === p.id
                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <><Send className="w-4 h-4" /> {p.token ? "Resend" : "Send"} Packet</>}
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1"
                      onClick={() => openAccept(p)}
                      data-testid={`pending-accept-btn-${p.id}`}>
                      <UserCheck className="w-4 h-4" /> Accept Child
                    </Button>
                    <Button variant="outline" size="sm"
                      className="text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                      onClick={() => openNeverEnrolled(p)}
                      data-testid={`pending-never-enrolled-btn-${p.id}`} title="Mark as Never Enrolled">
                      <UserX className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-4 py-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-600 mb-2">Returned Forms</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Object.entries(DOC_LABELS).map(([key, label]) => {
                        const done = uploaded.includes(key);
                        return (
                          <div key={key} className={`flex items-center justify-between text-xs px-3 py-2 rounded ${done ? "bg-green-50" : "bg-gray-50"}`}>
                            <span className="flex items-center gap-1.5 min-w-0">
                              {done ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <FileText className="w-3.5 h-3.5 text-gray-400" />}
                              <span className={`truncate ${done ? "text-green-700" : "text-gray-600"}`}>{label}</span>
                            </span>
                            {done && (
                              <button
                                className="text-teal-600 hover:underline ml-2 flex-shrink-0"
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/pending-enrollment/${p.id}/staged-document/${key}`, { responseType: "blob" });
                                    const blobUrl = URL.createObjectURL(res.data);
                                    window.open(blobUrl, "_blank");
                                  } catch (err) {
                                    toast.error("Could not open document");
                                  }
                                }}
                                data-testid={`pending-doc-view-${p.id}-${key}`}>
                                View
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {p.token && (
                      <p className="text-[11px] text-gray-400 mt-3 break-all">
                        Parent link: {window.location.origin}/pending-enrollment/{p.token}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Accepted / Archived history — separate selectable views (mirrors the
          existing Archived Children pattern: archived records never show under
          the accepted list). */}
      {(accepted.length > 0 || archived.length > 0 || neverEnrolled.length > 0) && (
        <div className="mt-6 border-t border-gray-100 pt-4" data-testid="enrollment-history-section">
          <Tabs defaultValue="accepted" className="w-full">
            <TabsList className="bg-transparent gap-2 p-0 h-auto mb-3">
              <TabsTrigger value="accepted"
                className="flex items-center gap-1.5 border border-green-200 text-green-700 bg-white rounded-lg px-3 py-1.5 text-xs font-medium data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:border-green-500"
                data-testid="history-tab-accepted">
                <UserCheck className="w-3.5 h-3.5" /> Accepted History ({accepted.length})
              </TabsTrigger>
              <TabsTrigger value="archived"
                className="flex items-center gap-1.5 border border-gray-200 text-gray-500 bg-white rounded-lg px-3 py-1.5 text-xs font-medium data-[state=active]:bg-gray-500 data-[state=active]:text-white data-[state=active]:border-gray-400"
                data-testid="history-tab-archived">
                <Archive className="w-3.5 h-3.5" /> Archived ({archived.length})
              </TabsTrigger>
              <TabsTrigger value="neverEnrolled"
                className="flex items-center gap-1.5 border border-slate-200 text-slate-500 bg-white rounded-lg px-3 py-1.5 text-xs font-medium data-[state=active]:bg-slate-500 data-[state=active]:text-white data-[state=active]:border-slate-400"
                data-testid="history-tab-never-enrolled">
                <UserX className="w-3.5 h-3.5" /> Never Enrolled ({neverEnrolled.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="accepted">
              {accepted.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-xs" data-testid="accepted-empty-state">
                  No accepted enrollments yet.
                </div>
              ) : (
                <div className="space-y-2" data-testid="accepted-history-section">
                  {accepted.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs bg-green-50 border border-green-100 rounded px-3 py-2"
                      data-testid={`accepted-row-${a.id}`}>
                      <span className="font-medium text-green-800">{a.childName}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-green-600">
                          Accepted {a.acceptedAt ? new Date(a.acceptedAt).toLocaleDateString() : ""}
                        </span>
                        <Button variant="ghost" size="sm"
                          className="h-7 px-2 text-green-700 hover:text-green-900 hover:bg-green-100 gap-1"
                          disabled={archivingId === a.id}
                          onClick={() => handleArchive(a)}
                          data-testid={`accepted-archive-btn-${a.id}`}>
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="archived">
              {archived.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-xs" data-testid="archived-empty-state">
                  No archived enrollments.
                </div>
              ) : (
                <div data-testid="archived-history-section">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-[11px] text-gray-400">
                      Archived enrollments are hidden from Accepted History. Restoring returns a record to Accepted History. Clearing only hides the entry — child records and documents are not deleted.
                    </p>
                    <Button variant="outline" size="sm"
                      className="h-7 px-2 flex-shrink-0 text-gray-600 border-gray-300 hover:bg-gray-100 gap-1"
                      disabled={clearingAll}
                      onClick={handleClearAll}
                      data-testid="archived-clear-all-btn">
                      <Trash2 className="w-3.5 h-3.5" /> Clear Archive
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {archived.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2"
                        data-testid={`archived-row-${a.id}`}>
                        <span className="font-medium text-gray-600">{a.childName}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">
                            {a.archivedAt
                              ? `Archived ${new Date(a.archivedAt).toLocaleDateString()}`
                              : (a.acceptedAt ? `Accepted ${new Date(a.acceptedAt).toLocaleDateString()}` : "")}
                          </span>
                          <Button variant="ghost" size="sm"
                            className="h-7 px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 gap-1"
                            disabled={archivingId === a.id}
                            onClick={() => handleRestore(a)}
                            data-testid={`archived-restore-btn-${a.id}`}>
                            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                          </Button>
                          <Button variant="ghost" size="sm"
                            className="h-7 px-2 text-gray-500 hover:text-gray-800 hover:bg-gray-200 gap-1"
                            disabled={archivingId === a.id}
                            onClick={() => handleClear(a)}
                            data-testid={`archived-clear-btn-${a.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Clear
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="neverEnrolled">
              {neverEnrolled.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-xs" data-testid="never-enrolled-empty-state">
                  No enrollments marked Never Enrolled.
                </div>
              ) : (
                <div data-testid="never-enrolled-section">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <p className="text-[11px] text-gray-400">
                      These families never completed enrollment. Nothing is deleted — restore a record anytime to put it back on your active Pending list, or clear the entry just to hide it here.
                    </p>
                    <Button variant="outline" size="sm"
                      className="h-7 px-2 flex-shrink-0 text-gray-600 border-gray-300 hover:bg-gray-100 gap-1"
                      disabled={clearingAllNeverEnrolled}
                      onClick={handleClearAllNeverEnrolled}
                      data-testid="never-enrolled-clear-all-btn">
                      <Trash2 className="w-3.5 h-3.5" /> Clear List
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {neverEnrolled.map((a) => (
                      <div key={a.id} className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2"
                        data-testid={`never-enrolled-row-${a.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">{a.childName}</span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-medium">
                              <UserX className="w-3 h-3" /> Never Enrolled
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-slate-400">
                              {a.archivedAt ? `Marked ${new Date(a.archivedAt).toLocaleDateString()}` : ""}
                            </span>
                            <Button variant="ghost" size="sm"
                              className="h-7 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200 gap-1"
                              disabled={archivingId === a.id}
                              onClick={() => handleRestorePending(a)}
                              data-testid={`never-enrolled-restore-btn-${a.id}`}>
                              <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                            </Button>
                            <Button variant="ghost" size="sm"
                              className="h-7 px-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200 gap-1"
                              disabled={archivingId === a.id}
                              onClick={() => handleClearNeverEnrolled(a)}
                              data-testid={`never-enrolled-clear-btn-${a.id}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Clear
                            </Button>
                          </div>
                        </div>
                        {a.archiveReason && (
                          <p className="mt-1.5 text-slate-500 italic" data-testid={`never-enrolled-reason-${a.id}`}>
                            Reason: {a.archiveReason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Send packet confirmation — mirrors the existing Enrollment Packet Send/Resend flow */}
      <AlertDialog open={!!sendTarget} onOpenChange={(o) => { if (!o) setSendTarget(null); }}>
        <AlertDialogContent data-testid="send-packet-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {sendTarget?.token ? "Resend Enrollment Packet?" : "Send Enrollment Packet?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  You are about to {sendTarget?.token ? "resend" : "send"} the enrollment packet to{" "}
                  <strong>
                    {sendTarget?.childName?.includes(",")
                      ? sendTarget.childName.split(",").reverse().map((s) => s.trim()).join(" ")
                      : sendTarget?.childName}
                  </strong>'s parent.
                </p>
                {sendTarget && (sendTarget.uploadedDocTypes?.length > 0) && (
                  <p className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-green-800 text-xs">
                    This family has already returned{" "}
                    <strong>
                      {sendTarget.uploadedDocTypes.length} of {(sendTarget.selectedForms?.length) || STANDARD_FORM_COUNT}
                    </strong>{" "}
                    forms. Their existing submissions will not be affected — this only resends the link.
                  </p>
                )}
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Send via:</p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={sendViaEmail}
                      onChange={(e) => setSendViaEmail(e.target.checked)}
                      className="w-4 h-4 accent-teal-600"
                      data-testid="send-via-email-toggle" />
                    <span className="text-sm text-gray-700 font-medium">Email</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={sendViaSMS}
                      onChange={(e) => setSendViaSMS(e.target.checked)}
                      className="w-4 h-4 accent-teal-600"
                      data-testid="send-via-sms-toggle" />
                    <span className="text-sm text-gray-700 font-medium">SMS</span>
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSendTarget(null)} data-testid="send-packet-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
              disabled={!sendViaEmail && !sendViaSMS}
              onClick={() => handleSend(sendTarget)}
              data-testid="send-packet-confirm-btn">
              {sendTarget?.token ? "Yes, Resend Packet" : "Yes, Send Packet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Accept Child dialog */}
      <Dialog open={!!acceptTarget} onOpenChange={(o) => { if (!o) { setAcceptTarget(null); setAcceptCategory(""); setAcceptSubcategory(""); } }}>
        <DialogContent data-testid="accept-child-dialog">
          <DialogHeader>
            <DialogTitle>Accept Child</DialogTitle>
            <DialogDescription>
              This creates a normal enrolled child record for <strong>{acceptTarget?.childName}</strong>. Returned
              documents stay saved and are handled by your existing records system afterward.
            </DialogDescription>
          </DialogHeader>
          {acceptTarget && !acceptTarget.category && (
            <div>
              <Label htmlFor="accept-cat">Category *</Label>
              <Select value={acceptCategory} onValueChange={(v) => { setAcceptCategory(v); if (v !== "full_time") setAcceptSubcategory(""); }}>
                <SelectTrigger id="accept-cat" data-testid="accept-category-select">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">This pending enrollment has no category — please choose one.</p>
            </div>
          )}
          {acceptTarget && (acceptTarget.category || acceptCategory) === "full_time" && (
            <div className="mt-3 border border-blue-200 bg-blue-50 rounded-lg p-3">
              <Label htmlFor="accept-age" className="font-semibold">Age Group (Optional)</Label>
              <Select value={acceptSubcategory} onValueChange={setAcceptSubcategory}>
                <SelectTrigger id="accept-age" data-testid="accept-age-group-select">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUP_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-700 mt-1">Since this child is Full Time, please also pick their Age Group here — you can also assign this later in Edit Child.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAcceptTarget(null); setAcceptCategory(""); setAcceptSubcategory(""); }}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={accepting}
              onClick={handleAccept} data-testid="accept-child-submit-btn">
              {accepting ? "Accepting…" : "Accept Child"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Never Enrolled dialog — optional short reason note before archiving */}
      <Dialog open={!!neverEnrolledTarget} onOpenChange={(o) => { if (!o) { setNeverEnrolledTarget(null); setNeverEnrolledReason(""); } }}>
        <DialogContent data-testid="never-enrolled-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Never Enrolled?</DialogTitle>
            <DialogDescription>
              <strong>{neverEnrolledTarget?.childName}</strong> will move to the Never Enrolled list and leave your
              active Pending list. Nothing is deleted — any returned documents stay saved, and you can restore this
              record anytime.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="never-enrolled-reason">Reason (Optional)</Label>
            <Textarea id="never-enrolled-reason" rows={3} placeholder="e.g. chose another center, stopped responding…"
              value={neverEnrolledReason}
              onChange={(e) => setNeverEnrolledReason(e.target.value)}
              data-testid="never-enrolled-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNeverEnrolledTarget(null); setNeverEnrolledReason(""); }}>
              Cancel
            </Button>
            <Button className="bg-gray-600 hover:bg-gray-700 text-white" disabled={markingNeverEnrolled}
              onClick={submitNeverEnrolled} data-testid="never-enrolled-submit-btn">
              {markingNeverEnrolled ? "Saving…" : "Mark Never Enrolled"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — fix a missing/incorrect parent email or phone, or child name */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent data-testid="edit-pending-dialog">
          <DialogHeader>
            <DialogTitle>Edit Pending Enrollment</DialogTitle>
            <DialogDescription>
              Update the child's name or parent contact info. This does not affect any documents already returned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="edit-childName">Child Name *</Label>
              <Input id="edit-childName" value={editForm.childName}
                onChange={(e) => setEditForm({ ...editForm, childName: e.target.value })}
                data-testid="edit-input-childName" />
            </div>
            <div>
              <Label htmlFor="edit-parentName">Parent/Guardian Name</Label>
              <Input id="edit-parentName" value={editForm.parentName}
                onChange={(e) => setEditForm({ ...editForm, parentName: e.target.value })}
                data-testid="edit-input-parentName" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-parentEmail">Parent Email</Label>
                <Input id="edit-parentEmail" type="email" value={editForm.parentEmail}
                  onChange={(e) => setEditForm({ ...editForm, parentEmail: e.target.value })}
                  data-testid="edit-input-parentEmail" />
              </div>
              <div>
                <Label htmlFor="edit-parentPhone">Parent Phone</Label>
                <Input id="edit-parentPhone" value={editForm.parentPhone}
                  onChange={(e) => setEditForm({ ...editForm, parentPhone: e.target.value })}
                  data-testid="edit-input-parentPhone" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" disabled={savingEdit}
              onClick={handleEditSave} data-testid="edit-pending-save-btn">
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="pending-create-dialog">
          <DialogHeader>
            <DialogTitle>New Pending Enrollment</DialogTitle>
            <DialogDescription>
              Enter the child’s name and a parent/guardian contact. This does not create a child record yet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pe-first">First Name *</Label>
                <Input id="pe-first" value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  data-testid="pending-input-firstName" />
              </div>
              <div>
                <Label htmlFor="pe-last">Last Name *</Label>
                <Input id="pe-last" value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  data-testid="pending-input-lastName" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pe-cat">Category</Label>
                <Select value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v, fullTimeSubcategory: v === "full_time" ? form.fullTimeSubcategory : "" })}>
                  <SelectTrigger id="pe-cat" data-testid="pending-input-category"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pe-bd">Birthdate</Label>
                <Input id="pe-bd" type="date" value={form.birthdate}
                  onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                  data-testid="pending-input-birthdate" />
              </div>
            </div>
            {form.category === "full_time" && (
              <div>
                <Label htmlFor="pe-age">Age Group (Optional)</Label>
                <Select value={form.fullTimeSubcategory}
                  onValueChange={(v) => setForm({ ...form, fullTimeSubcategory: v })}>
                  <SelectTrigger id="pe-age" data-testid="pending-input-ageGroup"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {AGE_GROUP_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="pe-pname">Parent/Guardian Name</Label>
              <Input id="pe-pname" value={form.parentName}
                onChange={(e) => setForm({ ...form, parentName: e.target.value })}
                data-testid="pending-input-parentName" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pe-email">Parent Email</Label>
                <Input id="pe-email" type="email" value={form.parentEmail}
                  onChange={(e) => setForm({ ...form, parentEmail: e.target.value })}
                  data-testid="pending-input-parentEmail" />
              </div>
              <div>
                <Label htmlFor="pe-phone">Parent Phone</Label>
                <Input id="pe-phone" value={form.parentPhone}
                  onChange={(e) => setForm({ ...form, parentPhone: e.target.value })}
                  data-testid="pending-input-parentPhone" />
              </div>
            </div>
            <p className="text-xs text-gray-400">A parent email or phone is needed to send the packet.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" disabled={saving}
              onClick={handleCreate} data-testid="pending-create-submit-btn">
              {saving ? "Creating…" : "Create Pending Enrollment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
